import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin/ui';
import { Icon } from '@glade-ai/ui';
import React, { type ComponentPropsWithoutRef, type FC, forwardRef, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import SearchInput from '@/components/FormFields/SearchInput';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import type * as ApiModels from '@/typings/api-models';

import s from '../InputComposer.module.scss';

type Props = {
  onAttach: (data: { title: string; invoiceTemplateId: string }) => void;
};

const ADD_INVOICE_LABEL = 'Invoice';
const ADD_INVOICE_ICON = 'ph--hand-coins--regular';

export const AddInvoiceMenuItem = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>, 'icon' | 'label'>
>((props, forwardedRef) => (
  <ChatComposeBoxAddAttachmentMenuItem
    {...props}
    ref={forwardedRef}
    icon={ADD_INVOICE_ICON}
    label={ADD_INVOICE_LABEL}
  />
));
AddInvoiceMenuItem.displayName = 'AddInvoiceMenuItem';

export const AddInvoicePopoverContent: FC<Props> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [search, setSearch] = useState('');
  const {
    data: invoiceTemplates,
    getData: getInvoiceTemplates,
    fetchingState: { isFetching: isFetchingInvoiceTemplates },
  } = useNoodleApi(tsClient.invoices.getInvoiceRequestTemplates);

  useEffect(() => {
    if (creatorId) {
      getInvoiceTemplates({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getInvoiceTemplates]);

  const handleClick = (template: Pick<ApiModels.InvoiceTemplate, 'id' | 'title'>): void => {
    onAttach({ invoiceTemplateId: template.id, title: template.title });
  };

  if (invoiceTemplates?.items.length === 0) {
    return null;
  }

  return (
    <>
      {!invoiceTemplates || isFetchingInvoiceTemplates ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <SearchInput
            label='Filter by title'
            placeholder='Type a title...'
            setSearch={setSearch}
            className={s.search}
          />
          {invoiceTemplates.items
            .filter((template) => template.title.toLowerCase().includes(search.toLowerCase()))
            .map((template) => (
              <Buttons
                className={s.documentRequestButton}
                key={template.id}
                isWrapper
                isFlat
                onClick={() => handleClick(template)}
              >
                <Icon icon='ph--hand-coins--regular' size={3.5} />
                <p>{template.title}</p>
              </Buttons>
            ))}
        </>
      )}
    </>
  );
};
